import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { useDispatch } from "react-redux";
import { fetchRoles } from "../../../../api/users";
import { useEffect, useRef, useState } from "react";
import PMSwitch from "../../../utils/PMSwitch";
import UppercaseFirstLetter, { dispatchWithToast } from "../../../Utils";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { updateUserAsync } from "../../../../slices/user";
import { parseRoleName } from "../../../../utils/parseRoleName";
import { buildRoleFormPayloadFromTree } from "../../../../utils/buildRoleFormPayloadFromTree";
import { getCompanyUserRoleHelp } from "../../../../utils/companyUserRolesRoleHelp";

function RoleUserHelpText({ roleName, fallbackDescription = null }) {
  const { t, i18n } = useTranslation();
  const block = getCompanyUserRoleHelp(i18n.language, roleName);
  const hasCurated =
    block && (Boolean(block.includes) || Boolean(block.adminNote));
  if (hasCurated) {
    return (
      <div className="mt-1 space-y-1.5 text-xs leading-snug text-gray-600">
        {block.includes ? (
          <p>
            <span className="font-medium text-gray-700">
              {t("company.userRoles.helpLabel.scope")}
            </span>{" "}
            {block.includes}
          </p>
        ) : null}
        {block.adminNote ? (
          <p className="rounded border border-amber-100 bg-amber-50/80 px-2 py-1.5 text-gray-700">
            <span className="font-medium text-amber-900/90">
              {t("company.userRoles.helpLabel.admin")}
            </span>{" "}
            {block.adminNote}
          </p>
        ) : null}
      </div>
    );
  }
  const apiText =
    typeof fallbackDescription === "string" ? fallbackDescription.trim() : "";
  if (apiText) {
    return (
      <p className="mt-1 text-xs leading-snug text-gray-500">{apiText}</p>
    );
  }
  return null;
}

function SyncRolesToFormik({ roles, isLoading, itemToken, setFieldValue }) {
  const lastTokenRef = useRef(undefined);
  useEffect(() => {
    if (isLoading || !roles?.length || itemToken == null) return;
    if (lastTokenRef.current === itemToken) return;
    lastTokenRef.current = itemToken;
    setFieldValue("roles", buildRoleFormPayloadFromTree(roles));
  }, [isLoading, roles, itemToken, setFieldValue]);
  return null;
}

export const UserRoles = (props) => {
  const { t } = useTranslation();
  const {
    item,
    onRefresh,
    simulationMode = false,
    onSimulationApply,
    simulationItemKey,
  } = props;
  const dispatch = useDispatch();

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPMRoles = async () => {
    try {
      const res = await fetchRoles();
      if (res?.data?.data) {
        setMainRoles(res.data.data); // Call setMainRoles only if data exists
      } else {
        console.error("No roles data found");
        setIsLoading(false); // Stop loading if no data is found
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setIsLoading(false); // Stop loading on error
    }
  };

  // Find role by searching name
  const findUserRole = (name) => {
    const found = item?.roles?.find((role) => role.name === name);
    return found;
  };

  // Main Roles - Restructured for new hierarchy
  const setMainRoles = (roles) => {
    if (!Array.isArray(roles)) {
      console.error("Roles is not an array:", roles);
      setRoles([]);
      setIsLoading(false);
      return;
    }

    // Don't process roles if user data isn't loaded yet
    if (!item || !item.roles) {
      setRoles([]);
      setIsLoading(false);
      return;
    }


    // Group roles by main module
    const moduleGroups = {};

    // First, create modules for all user roles that don't exist in the database
    item.roles.forEach((userRole) => {
      const parsed = parseRoleName(userRole.name);

      if (parsed.type === "main" && !moduleGroups[parsed.module]) {
        moduleGroups[parsed.module] = {
          id: userRole.id || null,
          name: userRole.name,
          label: userRole.label,
          description: userRole.description,
          checked: true, // User has this role
          actions: [],
          submodules: {},
        };
      } else if (parsed.type === "submodule" && !moduleGroups[parsed.module]) {
        // Create parent module if it doesn't exist
        moduleGroups[parsed.module] = {
          name: parsed.module,
          checked: false,
          actions: [],
          submodules: {},
        };
      }

      if (parsed.type === "submodule") {
        // Ensure parent module exists
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }

        // Create sub-module
        if (!moduleGroups[parsed.module].submodules[parsed.submodule]) {
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            id: userRole.id || null,
            name: userRole.name,
            label: userRole.label,
            description: userRole.description,
            checked: true, // User has this role
            actions: [],
          };
        }
      }
    });

    // Then process all database roles
    roles.forEach((role) => {
      if (!role || !role.name) {
        console.warn("Invalid role found:", role);
        return;
      }

      const parsed = parseRoleName(role.name);

      if (parsed.type === "main") {
        // Main module
        if (!moduleGroups[parsed.module]) {
          const isChecked = findUserRole(role.name) ? true : false;
          moduleGroups[parsed.module] = {
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
            checked: isChecked,
            actions: [],
            submodules: {},
          };
        } else {
          // Update existing module with database info
          moduleGroups[parsed.module] = {
            ...moduleGroups[parsed.module],
            id: role.id,
            label: role.label,
            description: role.description,
          };
        }
      } else if (parsed.type === "action") {
        // Module action
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }
        const isChecked = findUserRole(role.name) ? true : false;
        moduleGroups[parsed.module].actions.push({
          id: role.id,
          name: role.name,
          label: role.label,
          description: role.description,
          checked: isChecked,
          action: parsed.action,
        });
      } else if (parsed.type === "submodule") {
        // Sub-module
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }
        if (!moduleGroups[parsed.module].submodules[parsed.submodule]) {
          const isChecked = findUserRole(role.name) ? true : false;
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            id: role.id,
            name: role.name,
            label: role.label,
            description: role.description,
            checked: isChecked,
            actions: [],
          };
        }
      } else if (parsed.type === "submodule_action") {
        // Sub-module action
        if (!moduleGroups[parsed.module]) {
          moduleGroups[parsed.module] = {
            name: parsed.module,
            checked: false,
            actions: [],
            submodules: {},
          };
        }
        if (!moduleGroups[parsed.module].submodules[parsed.submodule]) {
          moduleGroups[parsed.module].submodules[parsed.submodule] = {
            name: `${parsed.module}_${parsed.submodule}`,
            checked: false,
            actions: [],
          };
        }
        const isChecked = findUserRole(role.name) ? true : false;
        moduleGroups[parsed.module].submodules[parsed.submodule].actions.push({
          id: role.id,
          name: role.name,
          label: role.label,
          description: role.description,
          checked: isChecked,
          action: parsed.action,
        });
      }
    });

    // Convert to array format and set proper visual indicators
    const main = Object.values(moduleGroups).map((module) => {
      // Calculate if main module should be visually checked based on children
      const hasCheckedActions = (module.actions || []).some(
        (action) => action.checked
      );
      const hasCheckedSubmodules = Object.values(module.submodules || {}).some(
        (sub) => sub.checked
      );
      // Main module is checked if it's directly assigned OR if it has children assigned
      const mainModuleChecked =
        module.checked || hasCheckedActions || hasCheckedSubmodules;



      // Update submodules with proper visual indicators
      const updatedSubmodules = Object.values(module.submodules || {}).map(
        (submodule) => {
          // Sub-module checked state is independent of its actions
          // Sub-module represents read rights, so it stays checked if it was originally checked
          const submoduleChecked = submodule.checked; // Keep original state

          return {
            ...submodule,
            checked: submoduleChecked,
          };
        }
      );

      return {
        ...module,
        checked: mainModuleChecked,
        submodules: updatedSubmodules,
      };
    });

    setRoles(main);
    setIsLoading(false); // Ensure loading is stopped after roles are set
  };



  useEffect(() => {
    // If we have user data and roles are already loaded, reprocess them
    if (item && item.roles && roles.length > 0) {
      // We need to refetch roles to reprocess them with the user data
      fetchPMRoles();
    }
  }, [item]);

  useEffect(() => {
    fetchPMRoles(); // Fetch roles on mount
  }, []);

  const handleRoleChange = (e, formik) => {
    const roleName = e.target.name;
    const checked = e.target.checked;
    const parsed = parseRoleName(roleName);



    let newRoles = [...roles];

    if (parsed.type === "main") {
      // Main module toggle - only affects module-level actions, not sub-modules
      newRoles = roles.map((role) => {
        if (role.name === roleName) {
          return {
            ...role,
            checked: checked, // Actually toggle the main module
            actions: (role.actions || []).map((action) => ({
              ...action,
              checked: checked,
            })),
            // Don't change submodules - they remain independent
          };
        }
        return role;
      });
    } else if (parsed.type === "action") {
      // Module action toggle
      newRoles = roles.map((role) => {
        if (role.name === parsed.module) {
          const newActions = (role.actions || []).map((action) =>
            action.name === roleName ? { ...action, checked: checked } : action
          );
          // Check if any module actions or sub-modules are selected
          const hasAnyChecked =
            newActions.some((action) => action.checked) ||
            (role.submodules || []).some((sub) => sub.checked);
          return {
            ...role,
            checked: hasAnyChecked, // Visual indicator only
            actions: newActions,
          };
        }
        return role;
      });
    } else if (parsed.type === "submodule") {
      // Sub-module toggle - only affects sub-module actions
      newRoles = roles.map((role) => {
        if (role.name === parsed.module) {
          const newSubmodules = (role.submodules || []).map((submodule) =>
            submodule.name === roleName
              ? {
                  ...submodule,
                  checked: checked,
                  // Toggle all sub-module actions
                  actions: (submodule.actions || []).map((action) => ({
                    ...action,
                    checked: checked,
                  })),
                }
              : submodule
          );
          // Check if any module actions or sub-modules are selected
          const hasAnyChecked =
            (role.actions || []).some((action) => action.checked) ||
            newSubmodules.some((sub) => sub.checked);
          return {
            ...role,
            checked: hasAnyChecked, // Visual indicator only
            submodules: newSubmodules,
          };
        }
        return role;
      });
    } else if (parsed.type === "submodule_action") {
      // Sub-module action toggle
      newRoles = roles.map((role) => {
        if (role.name === parsed.module) {
          const newSubmodules = (role.submodules || []).map((submodule) => {
            // Check if this is the correct submodule (using the full name)
            if (submodule.name === `${parsed.module}_${parsed.submodule}`) {
              const newActions = (submodule.actions || []).map((action) =>
                action.name === roleName
                  ? { ...action, checked: checked }
                  : action
              );
              // Sub-module remains checked (read rights) regardless of action states
              // Only uncheck if the sub-module itself was unchecked
              const submoduleChecked = submodule.checked; // Keep sub-module state unchanged
              return {
                ...submodule,
                checked: submoduleChecked,
                actions: newActions,
              };
            }
            return submodule;
          });
          // Check if any module actions or sub-modules are selected
          const hasAnyChecked =
            (role.actions || []).some((action) => action.checked) ||
            newSubmodules.some((sub) => sub.checked);
          return {
            ...role,
            checked: hasAnyChecked, // Visual indicator only
            submodules: newSubmodules,
          };
        }
        return role;
      });
    }

    // Update the state first
    setRoles(newRoles);

    // Update formik values with the new state
    const allRoles = [];
    newRoles.forEach((module) => {
      // Always include main module (regardless of checked state)
      allRoles.push({
        id: module.id,
        name: module.name,
        active: module.checked,
      });

      // Include all module actions (regardless of checked state)
      (module.actions || []).forEach((action) => {
        allRoles.push({
          id: action.id,
          name: action.name,
          active: action.checked,
        });
      });

      // Include all sub-modules and their actions (regardless of checked state)
      (module.submodules || []).forEach((submodule) => {
        allRoles.push({
          id: submodule.id,
          name: submodule.name,
          active: submodule.checked,
        });
        (submodule.actions || []).forEach((action) => {
          allRoles.push({
            id: action.id,
            name: action.name,
            active: action.checked,
          });
        });
      });
    });

    
    formik.setFieldValue("roles", allRoles);
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <>
      <Formik
        initialValues={{
          id: item?.id,
          approved: simulationMode ? true : item?.approved,
          submit: null,
          roles: [],
        }}
        onSubmit={(values, helpers) => {
          if (simulationMode) {
            const active = (values.roles || [])
              .filter((r) => r.active)
              .map((r) => r.name);
            onSimulationApply?.(active);
            helpers.setSubmitting(false);
            return;
          }
          try {
            dispatchWithToast(dispatch(updateUserAsync(values)), onRefresh);
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
          } catch (err) {
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err.message });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form>
            <SyncRolesToFormik
              roles={roles}
              isLoading={isLoading}
              itemToken={
                simulationMode
                  ? simulationItemKey ?? `sim-${item?.id}`
                  : item?.id
              }
              setFieldValue={formik.setFieldValue}
            />
            {!simulationMode && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                <div className=" bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h3 className="flex text-lg font-medium leading-6 text-gray-900">
                      <PMSwitch name="approved" {...formik} />
                      {t("Has access")}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("company.userRoles.accessIntro")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(simulationMode || formik.values.approved) &&
              roles.map((module, idx) => {
                const submoduleSectionOpen =
                  (module.submodules || []).length > 0 &&
                  (module.checked ||
                    (module.submodules || []).some((s) => s.checked));
                return (
                <div
                  key={idx}
                  className="mt-5 shadow sm:overflow-hidden sm:rounded-md"
                >
                  <div className="bg-white py-6 px-4 sm:p-6">
                    <div>
                      <h3 className="flex text-lg font-medium leading-6 text-gray-900">
                        <PMSwitch
                          name={module.name}
                          checked={module.checked}
                          onChange={(e) => handleRoleChange(e, formik)}
                        />
                        <UppercaseFirstLetter
                          text={t(module.name, {
                            defaultValue: module.label || module.name,
                          })}
                        />
                      </h3>
                      <RoleUserHelpText
                        roleName={module.name}
                        fallbackDescription={module.description}
                      />
                    </div>

                    {/* Module Actions */}
                    {module.checked && (module.actions || []).length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          {t("company.userRoles.sections.actions")}
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          {module.actions.map((action, actionIdx) => (
                            <div
                              key={actionIdx}
                              className="flex flex-col gap-0.5 rounded-md border border-gray-100 bg-gray-50/40 p-2"
                            >
                              <div className="flex items-center">
                                <PMSwitch
                                  name={action.name}
                                  checked={action.checked}
                                  onChange={(e) => handleRoleChange(e, formik)}
                                />
                                <UppercaseFirstLetter
                                  text={t(action.action)}
                                  className="ml-2"
                                />
                              </div>
                              <RoleUserHelpText
                                roleName={action.name}
                                fallbackDescription={action.description}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sub-modules (e.g. driving_trips without toggling parent «driving») */}
                    {submoduleSectionOpen && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          {t("company.userRoles.sections.submodules")}
                        </h4>
                        {module.submodules.map((submodule, subIdx) => (
                          <div
                            key={subIdx}
                            className="border-l-2 border-gray-200 pl-4 mb-4"
                          >
                            <div className="flex flex-col gap-0.5 mb-2">
                              <div className="flex items-center">
                                <ArrowRightIcon className="h-4 w-4 text-gray-400 mr-2 shrink-0" />
                                <PMSwitch
                                  name={submodule.name}
                                  checked={submodule.checked}
                                  onChange={(e) => handleRoleChange(e, formik)}
                                />
                                <UppercaseFirstLetter
                                  text={t(submodule.name, {
                                    defaultValue:
                                      submodule.label ||
                                      submodule.name.split("_")[1] ||
                                      submodule.name,
                                  })}
                                  className="ml-2"
                                />
                              </div>
                              <div className="ml-6">
                                <RoleUserHelpText
                                  roleName={submodule.name}
                                  fallbackDescription={submodule.description}
                                />
                              </div>
                            </div>

                            {/* Sub-module Actions */}
                            {submodule.checked &&
                              (submodule.actions || []).length > 0 && (
                                <div className="ml-6 mt-2">
                                  <div className="grid grid-cols-2 gap-4">
                                    {submodule.actions.map(
                                      (action, actionIdx) => (
                                        <div
                                          key={actionIdx}
                                          className="flex flex-col gap-0.5 rounded-md border border-gray-100 bg-gray-50/40 p-2"
                                        >
                                          <div className="flex items-center">
                                            <PMSwitch
                                              name={action.name}
                                              checked={action.checked}
                                              onChange={(e) =>
                                                handleRoleChange(e, formik)
                                              }
                                            />
                                            <UppercaseFirstLetter
                                              text={t(action.action)}
                                              className="ml-2"
                                            />
                                          </div>
                                          <RoleUserHelpText
                                            roleName={action.name}
                                            fallbackDescription={
                                              action.description
                                            }
                                          />
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
              })}
            {!simulationMode && <FormikSaveBar {...formik} />}
            {simulationMode && (
              <div className="mt-4 flex justify-end border-t border-gray-100 pt-4">
                <button
                  type="submit"
                  className="rounded-md bg-violet-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
                >
                  {t("platformBroadcast.dev.roleViewCustomApply")}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};
